import { useEffect, useState } from "react";
import api from "@/lib/apiClient";
import PageHeader from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, Users, Target, Trophy, IndianRupee, TrendingUp, Percent } from "lucide-react";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const STAGE_COLORS = ["#93C5FD", "#60A5FA", "#3B82F6", "#2563EB", "#1D4ED8", "#F59E0B", "#22C55E", "#EF4444"];
const PAY_STYLE = { Pending: "bg-amber-100 text-amber-700", Partial: "bg-blue-100 text-blue-700", Paid: "bg-accent/10 text-accent", Overdue: "bg-red-100 text-red-600", Refunded: "bg-slate-100 text-slate-600" };

const KPI = ({ icon: Icon, label, value, accent }) => (
  <div className="rounded-xl border border-border bg-card p-4">
    <div className="flex items-center gap-2 text-muted-foreground"><Icon className={`h-4 w-4 ${accent || "text-primary"}`} /><span className="text-xs">{label}</span></div>
    <p className="mt-1 font-display text-2xl font-bold text-secondary">{value}</p>
  </div>
);

export default function Sales() {
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/sales/overview").then(({ data }) => setData(data)); }, []);

  if (!data) return <div className="flex h-[60vh] items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const k = data.kpis;
  const maxFunnel = Math.max(...data.funnel.map((s) => s.count), 1);

  return (
    <div data-testid="sales-page">
      <PageHeader title="Sales" subtitle="Pipeline health, conversions and counsellor performance." />

      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <KPI icon={Users} label="Total Leads" value={k.total_leads} />
        <KPI icon={Target} label="Open Pipeline" value={k.open_opportunities} accent="text-blue-600" />
        <KPI icon={TrendingUp} label="Converted" value={k.converted} accent="text-accent" />
        <KPI icon={Percent} label="Conversion" value={`${k.conversion_rate}%`} />
        <KPI icon={IndianRupee} label="Revenue (mo)" value={inr(k.revenue_won)} accent="text-accent" />
        <KPI icon={Trophy} label="Enrollments" value={k.total_enrollments} accent="text-amber-600" />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Funnel */}
        <div className="rounded-xl border border-border bg-card p-6" data-testid="sales-funnel">
          <h3 className="mb-4 font-display text-lg font-semibold text-secondary">Sales funnel</h3>
          <div className="space-y-3">
            {data.funnel.map((s, i) => (
              <div key={s.stage}>
                <div className="mb-1 flex items-center justify-between text-sm"><span className="text-secondary">{s.stage}</span><span className="font-medium text-muted-foreground">{s.count}</span></div>
                <div className="h-2.5 w-full overflow-hidden rounded-full bg-slate-100">
                  <div className="h-full rounded-full transition-all" style={{ width: `${(s.count / maxFunnel) * 100}%`, backgroundColor: STAGE_COLORS[i] }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Leaderboard */}
        <div className="rounded-xl border border-border bg-card p-6" data-testid="sales-leaderboard">
          <h3 className="mb-4 font-display text-lg font-semibold text-secondary">Counsellor leaderboard</h3>
          {data.leaderboard.length === 0 ? <p className="text-sm text-muted-foreground">No enrollments yet.</p> : (
            <div className="space-y-2">
              {data.leaderboard.map((c, i) => (
                <div key={c.name + i} className="flex items-center justify-between rounded-lg border border-border p-3">
                  <span className="flex items-center gap-3">
                    <span className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold ${i === 0 ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}>{i + 1}</span>
                    <span className="font-medium text-secondary">{c.name}</span>
                  </span>
                  <span className="text-sm text-muted-foreground">{c.enrollments} enroll · <span className="font-medium text-secondary">{inr(c.revenue)}</span></span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Recent closures */}
      <div className="mt-6 rounded-xl border border-border bg-card" data-testid="sales-closures">
        <div className="border-b border-border px-6 py-4"><h3 className="font-display text-lg font-semibold text-secondary">Recent closures</h3></div>
        <Table>
          <TableHeader><TableRow><TableHead>Code</TableHead><TableHead>Student</TableHead><TableHead>Course</TableHead><TableHead>Fee</TableHead><TableHead>Date</TableHead><TableHead>Payment</TableHead></TableRow></TableHeader>
          <TableBody>
            {data.recent_closures.length === 0 && <TableRow><TableCell colSpan={6} className="py-8 text-center text-muted-foreground">No closures yet.</TableCell></TableRow>}
            {data.recent_closures.map((c) => (
              <TableRow key={c.enrollment_code}>
                <TableCell className="font-medium">{c.enrollment_code}</TableCell>
                <TableCell>{c.student_name}</TableCell>
                <TableCell className="text-sm">{c.course_name}</TableCell>
                <TableCell>{inr(c.final_fee)}</TableCell>
                <TableCell className="text-sm">{c.admission_date}</TableCell>
                <TableCell><Badge className={`border-0 ${PAY_STYLE[c.payment_status] || ""}`}>{c.payment_status}</Badge></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
